import React, { useEffect, useState } from "react";
import toast from 'react-hot-toast';
import Routes from "../../routes";
import {
    BreadCrumbs,
    PageTitleWithToolTip,
    DataGrid,
    Select,
    PrimaryButton,
    AddUserButton,
    DeleteModal
} from "../Common";
import { StyledCellLink } from "../Common/DataGrid/CellComponents"
import { Grid, Tooltip } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { dataGridContainerProps, EmptyActionIcon, select_commonCellProps, text_commonCellProps } from "../Common/DataGrid/utils";
import { fetchFilterValues, enterGeekCommandCenterUserContext } from "../../utils";
import { useBreadCrumbs, useNavigate } from "../../hooks";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import CreateIcon from "@mui/icons-material/Create";
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import { Grey2 } from "../../color_utils";
import UserInfo from "../../UserInfo";

const pageTitle = "Manage All Users";

const ManageAllUsers = () => {
    const [filters, setFilters] = useState([]);
    const [instanceId, setInstanceId] = useState("");
    const [instances, setInstances] = useState([]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [refreshNeeded, setRefreshNeeded] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState({
        id: 0,
        visibility: false,
    });

    const navigate = useNavigate({ pageName: 'ManageAllUsers' });

    const breadCrumbs = useBreadCrumbs({
        path: { id: 'ManageAllUsers', text: 'Manage All Users', link: window.location.pathname }
    });

    const onDeleteUser = async () => {
        if (UserInfo.userId === showDeleteModal.id) {
            return;
        }

        try {
            const response = await fetch(Routes.User.DeleteUser(showDeleteModal.id), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });
            const data = await response.json();

            if (response.status !== 200 || !data) {
                return;
            }

            setRefreshNeeded((prev) => !prev);
            setShowDeleteModal({ id: 0, visibility: false });
            toast.success('The user has been successfully deleted', { duration: 6000 });
        } catch {
            console.error("something went wrong!");
        }
    };

    const columns = [
        {
            field: 'id',
            headerName: 'User ID',
            initiallyVisible: false,
            ...text_commonCellProps,
        },
        {
            field: 'name',
            headerName: 'Name',
            ...text_commonCellProps,
            renderCell: ({ row: { id, name } }) => (
                <StyledCellLink
                    primaryLink
                    path={`/User/${id}`}
                    text={name}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'userName',
            headerName: 'User Name',
            ...text_commonCellProps,
        },
        {
            field: 'company',
            headerName: 'Company',
            ...select_commonCellProps,
        },
        {
            field: 'userStatus',
            headerName: 'Status',
            ...select_commonCellProps,
            // valueOptions: userStatusOptions
        },
        {
            field: 'email',
            headerName: 'Email',
            ...text_commonCellProps,
        },
        {
            field: 'state',
            headerName: 'State',
            ...select_commonCellProps,
        },
        {
            field: 'country',
            headerName: 'Country',
            ...select_commonCellProps,
        },
        {
            field: 'userType',
            headerName: 'Type',
            ...select_commonCellProps,
        },
        {
            field: 'certificationType',
            headerName: 'Certification Type',
            ...select_commonCellProps,
        },
        {
            field: 'certificationNumber',
            headerName: 'Certification Number',
            ...text_commonCellProps,
        },
        {
            type: 'actions',
            field: 'Quick Actions',
            width: 100,
            getActions: ({ row: { id, userStatus } }) => {

                let defaultMenu = [
                    <GridActionsCellItem
                        key={"view-user"}
                        icon={
                            <Tooltip
                                title="View User"
                            >
                                <SearchIcon sx={{ color: Grey2 }} />
                            </Tooltip>
                        }
                        onClick={() => navigate.push(`/User/${id}`)}
                    />
                ]

                if (userStatus !== "Deleted") {
                    defaultMenu = [
                        ...defaultMenu,
                        <GridActionsCellItem
                            key={"edit-user"}
                            icon={
                                <Tooltip
                                    title="Edit User"
                                >
                                    <CreateIcon sx={{ color: Grey2 }} />
                                </Tooltip>
                            }
                            onClick={() => navigate.push(`/UserUpdate/${id}`)}
                        />,
                        <GridActionsCellItem
                            key={"delete-user"}
                            icon={
                                <Tooltip
                                    title="Delete User"
                                >
                                    <DeleteIcon sx={{ color: Grey2 }} />
                                </Tooltip>
                            }
                            onClick={() => setShowDeleteModal({ id, visibility: true })}
                        />
                    ]
                }
                else 
                {
                    defaultMenu = [
                        ...defaultMenu,
                        <EmptyActionIcon key={"empty-icon-1"} />,
                        <EmptyActionIcon key={"empty-icon-2"} />
                    ]
                }

                return defaultMenu;
            }
        },
    ];

    const fetchData = async () => {
        await Promise.all([
            fetchFilterValues(Routes.User.ManageAllUsersTableFilters()),
            fetch(Routes.Instance.GetActiveInstance())
        ])
            .then(async ([filters, rawInstances]) => {
                const instances = await rawInstances.json();
                setFilters(filters);
                setInstances(instances.map(i => ({ value: i.id, label: i.name })));
            })
            .finally(() => setLoading(false))
            .catch(err => {
                console.log('Error fetching Filters & Instances', err);
                toast.error('Could not fetch filters &  Instances');
            });
    };

    const handleMenuOpen = () => {
        // set instanceId before pushing user to AddUser
        if (menuOpen) {
            sessionStorage.setItem("createUserInstanceId", instanceId);
        }
        return setMenuOpen(prev => !prev);
    };

    useEffect(() => {
        // US 16905: user screens opened from here must not expose the Assigned Locations editor
        enterGeekCommandCenterUserContext();
        fetchData();
    }, []);

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
            />
            <Grid container direction="row">
                <Grid item xs={6} md={6} lg={6}>
                    <PageTitleWithToolTip
                        pageTitle={pageTitle}
                        toolTipText={"Manage All Users"} />
                </Grid>
                <Grid item xs={6} sx={{ justifyContent: "flex-end", display: "flex", alignItems: "baseline" }}>
                    <Select
                        label={"Company"}
                        variant={"outlined"}
                        width={50}
                        options={instances}
                        value={instanceId}
                        disabled={loading}
                        onChange={(event) => setInstanceId(event.target.value)}
                        sx={{ marginRight: 4, backgroundColor: "#fff" }}
                        labelTopMargin={0}
                        renderHelperText={!instanceId}
                        customHelperText={"Select a company to begin adding a user"}
                    />
                    <AddUserButton
                        setSessionStorage
                        openMenu={menuOpen}
                        onOpen={handleMenuOpen}
                        onClose={handleMenuOpen}
                        history={navigate}
                        customButton={
                            <PrimaryButton
                                disabled={loading || !instanceId}
                                buttonText={"Add User"}
                                onClick={handleMenuOpen}
                                StartIcon={ArrowBackIcon}
                                EndIcon={AddIcon}
                            />
                        }
                    />
                </Grid>
                <Grid item {...dataGridContainerProps}>
                    <DataGrid
                        url={Routes.User.ManageAllUsers()}
                        columns={columns}
                        exportButtonURL={Routes.User.ManageAllUsersExport()}
                        exportButtonAlt="Export Users"
                        filterOptions={filters}
                        refresh={refreshNeeded}
                    />
                </Grid>
            </Grid >
            <DeleteModal
                open={showDeleteModal.visibility}
                buttonText={"Yes, Delete"}
                title="Delete this user?"
                description="It will be permanently lost."
                onClose={() => setShowDeleteModal({ id: 0, visibility: false })}
                onConfirm={onDeleteUser}
            />
        </>
    );
};

export default ManageAllUsers;